import { useEffect, useMemo, useState } from 'react';
import { createWithEqualityFn as create } from 'zustand/traditional';
import type { CourseDto, MenuItemVariantDto, MenuSectionDto } from '../../generated-api';
import { useOpeningHoursStore, useServerTimeOffsetMs } from './OpeningHoursStore';
import { isWithinOpeningHours } from '../utils/openingHours';

/** Returns a minute-granularity timestamp that updates every minute */
function useMinuteTick(): number {
  const [tick, setTick] = useState(() => Math.floor(Date.now() / 60_000));

  useEffect(() => {
    const interval = setInterval(() => {
      setTick(Math.floor(Date.now() / 60_000));
    }, 60_000);
    return () => clearInterval(interval);
  }, []);

  return tick;
}

interface MenuSectionsActions {
  setMenuData: (
    sections: MenuSectionDto[],
    courses: CourseDto[],
    allSections: MenuSectionDto[],
    componentProducts: MenuItemVariantDto[],
    locationId: number
  ) => void;
  setLoading: (loading: boolean) => void;
  clear: () => void;
}

export interface MenuSectionsState {
  sections: MenuSectionDto[];
  allSections: MenuSectionDto[];
  courses: CourseDto[];
  componentProducts: MenuItemVariantDto[];
  isLoading: boolean;
  locationId: number | undefined;
  actions: MenuSectionsActions;
}

export const useMenuSectionsStore = create<MenuSectionsState>()((set) => ({
  actions: {
    clear: () =>
      set({
        sections: [],
        allSections: [],
        courses: [],
        componentProducts: [],
        locationId: undefined,
        isLoading: false,
      }),
    setLoading: (isLoading) => set({ isLoading }),
    setMenuData: (sections, courses, allSections, componentProducts, locationId) =>
      set({ sections, courses, allSections, componentProducts, locationId, isLoading: false }),
  },
  allSections: [],
  componentProducts: [],
  courses: [],
  isLoading: false,
  locationId: undefined,
  sections: [],
}));

export const useMenuCourses = () => useMenuSectionsStore((state) => state.courses);
export const useMenuSections = () => useMenuSectionsStore((state) => state.sections);
export const useAllMenuSections = () => useMenuSectionsStore((state) => state.allSections);
export const useMenuSectionsActions = () => useMenuSectionsStore((state) => state.actions);
export const useMenuSectionsLocationId = () => useMenuSectionsStore((state) => state.locationId);
export const useMenuComponentProducts = () => useMenuSectionsStore((state) => state.componentProducts);
/**
 * Filters the given sections by opening hours for the timezone.
 * Sections without opening hours restrictions are always included.
 * Re-evaluates every minute to handle sections closing during a session.
 */
const useSectionsWithinOpeningHours = (sections: MenuSectionDto[], zoneId: string): MenuSectionDto[] => {
  const openingHours = useOpeningHoursStore((state) => state.sectionOpeningHours);
  const serverTimeOffsetMs = useServerTimeOffsetMs();
  const minuteTick = useMinuteTick();

  return useMemo(() => {
    const now = new Date(Date.now() + serverTimeOffsetMs);
    return sections.filter((section) => {
      const hours = openingHours[section.sectionId];
      if (!hours || hours.length === 0) {
        return true;
      } // No restrictions
      return isWithinOpeningHours(hours, now, zoneId);
    });
    // MinuteTick triggers re-evaluation every minute for time-sensitive opening hours
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sections, openingHours, zoneId, minuteTick, serverTimeOffsetMs]);
};

/**
 * Returns the regular menu sections filtered by opening hours.
 * Excludes search-only sections. Used where only currently-open sections are
 * relevant (e.g. barcode matching) — the menu display shows closed sections
 * with a banner instead, so it uses the unfiltered lists.
 */
export const useActiveSections = (zoneId: string): MenuSectionDto[] => {
  const sections = useMenuSectionsStore((state) => state.sections);
  return useSectionsWithinOpeningHours(sections, zoneId);
};
